import { HttpClient, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import Env from '../../env';
import { BehaviorSubject, defer, from, Observable, of, Subscription, throwError, timer } from 'rxjs';
import { catchError, exhaustMap, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Preferences } from '@capacitor/preferences';
import { jwtDecode } from 'jwt-decode';
import MtbPlugin from './mtb.plugin';
import { Capacitor } from '@capacitor/core';
import { AuthService, USE_ANONYMOUS_ACCESS_TOKEN } from './auth.service';

@Injectable({ providedIn: 'root' })
export class AuthInterceptor implements HttpInterceptor {
    private readonly userAccessTokenKey = `${this.env.APP_NAME}-access`;
    private readonly userRefreshTokenKey = `${this.env.APP_NAME}-refresh-token`;
    private readonly userAccessToken$ = new BehaviorSubject<string>(null);

    private readonly deviceAccessTokenKey = `${this.env.APP_NAME}-device-access`;
    private readonly deviceRefreshTokenKey = `${this.env.APP_NAME}-device-refresh`;
    private readonly deviceAccessToken$ = new BehaviorSubject<string>(null);

    private userTokenRefreshSubscription: Subscription;
    private deviceTokenRefreshSubscription: Subscription;

    constructor(
        @Inject('ENV') private readonly env: Env,
        private readonly httpClient: HttpClient,
        private readonly authService: AuthService
    ) {
        if (!Capacitor.isNativePlatform()) {
            this.startUserTokenRefresh();
            this.startDeviceTokenRefresh();
        }

        this.authService.logoutBroadcast$.subscribe(() => {
            this.stopPolling();
        });
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const isRequestWithoutToken =
            req.url.includes('languages') ||
            req.url.includes('terms-and-conditions') ||
            req.url.startsWith('assets') ||
            req.url.includes('translations') ||
            req.url.includes('region-settings') ||
            req.url.includes('forgot-password') ||
            req.url.includes('oidc');

        if (isRequestWithoutToken) {
            return next.handle(req);
        }
        if (req.headers.has('Authorization')) {
            return next.handle(req);
        }
        if (req.headers.has('No-Auth')) {
            const headers = req.headers.delete('No-Auth');
            const cloned = req.clone({ headers });
            return next.handle(cloned);
        }

        if (req.context.get(USE_ANONYMOUS_ACCESS_TOKEN)) {
            return this.handleDeviceToken(req, next);
        }

        if (this.authService.getLoginStatus()) {
            return this.handleUserToken(req, next);
        } else if (this.env.DEVICE_CUSTOMER_ENABLED && this.shouldUseDeviceToken(req)) {
            return this.handleDeviceToken(req, next);
        }

        return next.handle(req);
    }

    private shouldUseDeviceToken(req: HttpRequest<any>): boolean {
        if (Capacitor.isNativePlatform()) {
            return true;
        }
        return this.isDevicePriorityEndpoint(req);
    }

    private isDevicePriorityEndpoint(req: HttpRequest<any>): boolean {
        const priorityEndpoints = this.env.DEVICE_AND_USER_PRIORITY_ENDPOINTS ?? [];
        const duplicateEndpoints = this.env.DEVICE_AND_USER_DUPLICATE_ENDPOINTS ?? [];
        return [...priorityEndpoints, ...duplicateEndpoints].some((value) => req.url.includes(value));
    }

    public startPolling() {
        this.startUserTokenRefresh();
        this.startDeviceTokenRefresh();
    }

    public stopPolling() {
        this.stopUserTokenRefresh();
        this.stopDeviceTokenRefresh();
    }

    private handleDeviceToken(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.deviceAccessToken$.pipe(
            filter((token) => token && !this.isTokenExpired(token)),
            take(1),
            switchMap((token) => next.handle(this.authorizeRequest(req, token))),
            catchError(this.handleAuthError)
        );
    }

    private handleUserToken(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.userAccessToken$.pipe(
            filter((token) => token && !this.isTokenExpired(token)),
            take(1),
            switchMap((token) => next.handle(this.authorizeRequest(req, token))),
            catchError(this.handleAuthError)
        );
    }

    private isTokenExpired(token: string): boolean {
        const expiration = jwtDecode(token).exp * 1000;
        const timeSkew = 30_000;
        return Date.now() >= expiration - timeSkew;
    }

    private authorizeRequest(request: HttpRequest<any>, token: string) {
        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`
            }
        });
    }

    private readonly handleAuthError = (e: any): Observable<any> => {
        const isLoginRequest = this.isLoginRequest(e.url);

        if (e?.error?.message === 'INVALID_GRANT' || e?.error?.message === 'SESSION_NOT_ACTIVE') {
            if (isLoginRequest) {
                if (e.status === 400) {
                    e.error.message = 'ACCOUNT_UNVERIFIED';
                }
                throw e;
            } else {
                console.warn('Session was killed');
                this.stopPolling();
                if (this.authService.getLoginStatus()) {
                    this.authService.signOut(true);
                }
                return of(null);
            }
        } else {
            console.error(e);
            throw e;
        }
    };

    private isLoginRequest(url: string): boolean {
        if (!url) {
            return false;
        }
        return url.includes('/v1/auth/login');
    }

    // Public method to restart token refresh on login
    public restartUserTokenRefresh() {
        this.startUserTokenRefresh();
    }

    private startDeviceTokenRefresh() {
        this.stopDeviceTokenRefresh();

        if (!this.env.DEVICE_CUSTOMER_ENABLED) {
            return;
        }

        const accessToken$ = Capacitor.isNativePlatform()
            ? timer(0, 5000).pipe(
                  switchMap(() => Preferences.get({ key: this.deviceAccessTokenKey })),
                  map((r) => r.value)
              )
            : timer(0, 5000).pipe(map(() => localStorage.getItem(this.deviceAccessTokenKey)));

        this.deviceTokenRefreshSubscription = accessToken$
            .pipe(
                exhaustMap((token) => {
                    if (!token || this.isTokenExpired(token)) {
                        return this.refreshDeviceSession();
                    }
                    return of(token);
                }),
                catchError(() => of(null))
            )
            .subscribe((token: string) => this.deviceAccessToken$.next(token));
    }

    private stopDeviceTokenRefresh() {
        if (this.deviceTokenRefreshSubscription) {
            this.deviceTokenRefreshSubscription.unsubscribe();
            this.deviceTokenRefreshSubscription = null;
        }
    }

    private getDeviceId$(): Observable<string> {
        if (Capacitor.isNativePlatform()) {
            return defer(() => MtbPlugin.deviceId()).pipe(map(({ deviceId }) => deviceId));
        }
        return throwError(() => new Error('Device customer is only available in the native mobile app'));
    }

    private getDeviceRefreshToken$(): Observable<string | null> {
        if (Capacitor.isNativePlatform()) {
            return from(Preferences.get({ key: this.deviceRefreshTokenKey })).pipe(map((r) => r.value));
        }
        return of(localStorage.getItem(this.deviceRefreshTokenKey));
    }

    private createDeviceCustomer$(): Observable<{ accessToken: string; refreshToken: string }> {
        return this.getDeviceId$().pipe(
            switchMap((deviceId) => {
                const url = `${this.env.WMB?.WMB_PUBLIC_API}/v1/device-customers/${deviceId}`;
                return this.httpClient.post<{ accessToken: string; refreshToken: string }>(url, null, {
                    headers: {
                        'No-Auth': 'true'
                    }
                });
            })
        );
    }

    private refreshDeviceSession(): Observable<string> {
        return this.getDeviceRefreshToken$().pipe(
            switchMap((token) => {
                if (token) {
                    const url = `${this.env.WMB?.WMB_PUBLIC_API}/v1/device-customers/refresh-token`;
                    const payload = {
                        refreshToken: token
                    };
                    return this.httpClient
                        .post<{ accessToken: string; refreshToken: string }>(url, payload, {
                            headers: {
                                'No-Auth': 'true'
                            }
                        })
                        .pipe(
                            catchError((error) => {
                                if ([404, 400].includes(error.status)) {
                                    console.warn(
                                        '[AuthInterceptor] Device refresh token failed with status',
                                        error.status,
                                        '- creating new device customer'
                                    );
                                    return this.createDeviceCustomer$();
                                }
                                throw error;
                            })
                        );
                }
                return this.createDeviceCustomer$();
            }),
            tap(async (tokens) => {
                await Preferences.set({ key: this.deviceRefreshTokenKey, value: tokens?.refreshToken });
                await Preferences.set({ key: this.deviceAccessTokenKey, value: tokens?.accessToken });
                localStorage.setItem(this.deviceRefreshTokenKey, tokens?.refreshToken);
                localStorage.setItem(this.deviceAccessTokenKey, tokens?.accessToken);
            }),
            map((tokens) => tokens?.accessToken),
            catchError(this.handleAuthError)
        );
    }

    private startUserTokenRefresh() {
        // Stop existing subscription if any
        if (this.userTokenRefreshSubscription) {
            this.userTokenRefreshSubscription.unsubscribe();
        }

        this.userTokenRefreshSubscription = timer(0, 5000)
            .pipe(
                switchMap(() => Preferences.get({ key: this.userRefreshTokenKey })),
                map((r) => r.value),
                filter((token) => token !== null),
                switchMap(() => Preferences.get({ key: this.userAccessTokenKey })),
                map((r) => r.value),
                exhaustMap((token) => {
                    if (token === null || this.isTokenExpired(token)) {
                        return this.refreshUserSession();
                    }
                    return of(token);
                }),
                catchError(() => of(null))
            )
            .subscribe((token: string) => this.userAccessToken$.next(token));
    }

    private stopUserTokenRefresh() {
        if (this.userTokenRefreshSubscription) {
            this.userTokenRefreshSubscription.unsubscribe();
            this.userTokenRefreshSubscription = null;
        }
    }

    private refreshUserSession(): Observable<string> {
        return from(Preferences.get({ key: this.userRefreshTokenKey })).pipe(
            map((r) => r.value),
            switchMap((token) => {
                const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/auth/refresh`;
                const payload = {
                    clientId: this.env.WMB.AUTH.CLIENT_ID,
                    refreshToken: token
                };
                return this.httpClient.post<{ accessToken: string; refreshToken: string }>(url, payload, {
                    headers: {
                        'No-Auth': 'true'
                    }
                });
            }),
            tap(async (tokens) => {
                await Preferences.set({ key: this.userRefreshTokenKey, value: tokens.refreshToken });
                await Preferences.set({ key: this.userAccessTokenKey, value: tokens.accessToken });
                localStorage.setItem(this.userRefreshTokenKey, tokens.refreshToken);
                localStorage.setItem(this.userAccessTokenKey, tokens.accessToken);
            }),
            map((tokens) => tokens.accessToken),
            catchError(this.handleAuthError)
        );
    }
}
